import { Component, OnInit, HostListener, PLATFORM_ID, Inject } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Meta, Title } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { BrandAssetsService } from '../../../../core/services/brand-assets.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

interface Section {
  id: string;
  title: string;
}

@Component({
  selector: 'app-brand-guidelines',
  templateUrl: './brand-guidelines.component.html',
  styleUrls: ['./brand-guidelines.component.css'],
  standalone: true,
  imports: [CommonModule, RouterModule]
})
export class BrandGuidelinesComponent implements OnInit {
  sections: Section[] = [
    { id: 'introduction', title: 'Introduction' },
    { id: 'brand-core', title: 'Brand Core' },
    { id: 'brand-identity', title: 'Brand Identity' },
    { id: 'brand-personality', title: 'Brand Personality' },
    { id: 'audience-personas', title: 'Audience Personas' },
    { id: 'application-guidelines', title: 'Application Guidelines' },
    { id: 'social-media', title: 'Social Media Strategy' },
    { id: 'legal', title: 'Legal and Compliance' },
    { id: 'internal-brand', title: 'Internal Brand Engagement' },
    { id: 'accessibility', title: 'Accessibility Standards' },
    { id: 'crisis', title: 'Crisis Communication' },
    { id: 'measurement', title: 'Measurement and Evaluation' },
    { id: 'contact', title: 'Contact Information' }
  ];

  activeSection: string = 'introduction';
  isMobileNavOpen = false;
  isDownloading = false;
  isTocVisible: boolean = false;
  private observer: IntersectionObserver | null = null;

  constructor(
    private meta: Meta,
    private title: Title,
    private brandAssetsService: BrandAssetsService,
    private sanitizer: DomSanitizer,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit() {
    this.setupSEO();
    if (isPlatformBrowser(this.platformId)) {
      this.setupIntersectionObserver();
      this.injectStructuredData();
    }
  }

  private setupSEO() {
    const baseUrl = 'https://www.industryintel.com'; // Replace with your actual domain
    const pageUrl = `${baseUrl}/brand-guidelines`;
    const imageUrl = 'https://images.unsplash.com/photo-1557426272-fc759fdf7a8d?q=80'; // Placeholder image URL

    // Basic SEO
    this.title.setTitle('Brand Guidelines | Industry Intelligence Inc. - Official Brand Standards');
    
    // Meta tags
    const metaTags = [
      { name: 'description', content: 'Official brand guidelines for Industry Intelligence Inc. Comprehensive guide covering logo usage, color palette, typography, brand standards, and corporate identity guidelines.' },
      { name: 'keywords', content: 'brand guidelines, Industry Intelligence, corporate identity, brand standards, logo usage, color palette, typography, brand personality' },
      { name: 'author', content: 'Industry Intelligence Inc.' },
      { name: 'robots', content: 'index, follow' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      { name: 'language', content: 'English' },
      
      // Open Graph tags
      { property: 'og:title', content: 'Industry Intelligence Brand Guidelines - Official Brand Standards' },
      { property: 'og:description', content: 'Access the official brand guidelines of Industry Intelligence Inc. Learn about our brand identity, standards, and usage guidelines.' },
      { property: 'og:image', content: imageUrl },
      { property: 'og:url', content: pageUrl },
      { property: 'og:type', content: 'website' },
      { property: 'og:site_name', content: 'Industry Intelligence Inc.' },
      
      // Twitter Card tags
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:site', content: '@IndustryIntel' },
      { name: 'twitter:title', content: 'Industry Intelligence Brand Guidelines' },
      { name: 'twitter:description', content: 'Official brand guidelines and standards for Industry Intelligence Inc.' },
      { name: 'twitter:image', content: imageUrl },
      
      // Additional SEO tags
      { name: 'revisit-after', content: '7 days' },
      { name: 'theme-color', content: '#1a73e8' },
      { property: 'article:modified_time', content: new Date().toISOString() },
      { name: 'format-detection', content: 'telephone=no' }
    ];

    metaTags.forEach(tag => {
      if (tag.name) {
        this.meta.updateTag({ name: tag.name, content: tag.content });
      } else if (tag.property) {
        this.meta.updateTag({ property: tag.property, content: tag.content });
      }
    });
  }

  private injectStructuredData(): void {
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Industry Intelligence Brand Guidelines',
      description: 'Official brand guidelines and standards for Industry Intelligence Inc.',
      publisher: {
        '@type': 'Organization',
        name: 'Industry Intelligence Inc.',
        logo: {
          '@type': 'ImageObject',
          url: 'https://images.unsplash.com/photo-1557426272-fc759fdf7a8d?q=80' // Replace with your actual logo URL
        }
      },
      mainEntity: {
        '@type': 'Article',
        headline: 'Industry Intelligence Brand Guidelines',
        author: {
          '@type': 'Organization',
          name: 'Industry Intelligence Inc.'
        },
        datePublished: new Date().toISOString(),
        dateModified: new Date().toISOString(),
        about: {
          '@type': 'Thing',
          name: 'Brand Guidelines'
        }
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);
  }

  private setupIntersectionObserver() {
    if (!isPlatformBrowser(this.platformId)) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.activeSection = entry.target.id;
          }
        });
      },
      { rootMargin: '-20% 0px -60% 0px' }
    );

    this.sections.forEach(section => {
      const element = document.getElementById(section.id);
      if (element) {
        this.observer?.observe(element);
      }
    });
  }

  scrollToSection(sectionId: string) {
    if (!isPlatformBrowser(this.platformId)) return;
    
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      this.activeSection = sectionId;
      this.isTocVisible = false;
    }
  }

  toggleMobileNav() {
    this.isMobileNavOpen = !this.isMobileNavOpen;
    if (this.isMobileNavOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }

  async downloadAsset(type: string) {
    if (this.isDownloading) return;
    
    this.isDownloading = true;
    const loadingToast = this.showToast('Preparing download...');
    
    try {
      await this.brandAssetsService.downloadAndSaveAsset(type, false);
      this.showToast('Download complete!', 'success');
    } catch (error) {
      console.error('Error downloading asset:', error);
      this.showToast('Failed to download asset. Please try again.', 'error');
    } finally {
      this.isDownloading = false;
      if (loadingToast) {
        loadingToast.remove();
      }
    }
  }

  private showToast(message: string, type: 'info' | 'success' | 'error' = 'info'): HTMLDivElement | null {
    if (!isPlatformBrowser(this.platformId)) return null;

    const toast = document.createElement('div');
    toast.className = `fixed bottom-4 right-4 px-6 py-3 rounded-lg text-white transition-all transform translate-y-0 opacity-100 z-50 ${
      type === 'success' ? 'bg-green-600' :
      type === 'error' ? 'bg-red-600' :
      'bg-blue-600'
    }`;
    toast.textContent = message;
    document.body.appendChild(toast);

    // Auto remove after 3 seconds for success/error messages
    if (type !== 'info') {
      setTimeout(() => {
        toast.classList.add('translate-y-full', 'opacity-0');
        setTimeout(() => toast.remove(), 300);
      }, 3000);
    }

    return toast;
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboardNav(event: KeyboardEvent) {
    if (event.key === 'Escape' && this.isMobileNavOpen) {
      this.toggleMobileNav();
    }
  }

  toggleToc() {
    this.isTocVisible = !this.isTocVisible;
  }

  ngOnDestroy() {
    if (this.observer && isPlatformBrowser(this.platformId)) {
      this.observer.disconnect();
    }
  }
} 